<template>
  <div class="delivery-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>发货单管理</span>
          <div>
            <el-button type="success" @click="handleExport" :loading="exportLoading">导出发货单</el-button>
            <el-button type="primary" @click="handleCreate">新增发货单</el-button>
          </div>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="关键词">
          <el-input
            v-model="queryParams.keyword"
            placeholder="发货单号/客户姓名"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="发货状态">
          <el-select v-model="queryParams.deliveryStatus" placeholder="全部" clearable style="width: 150px">
            <el-option label="待发货" value="PENDING" />
            <el-option label="已发货" value="DELIVERED" />
            <el-option label="已签收" value="RECEIVED" />
          </el-select>
        </el-form-item>
        <el-form-item label="物流公司">
          <el-select
            v-model="queryParams.logisticsCompany"
            placeholder="请选择物流公司"
            clearable
            filterable
            style="width: 200px"
          >
            <el-option
              v-for="logistics in logisticsCompanyList"
              :key="logistics.dataCode"
              :label="`${logistics.dataName}(${logistics.dataCode})`"
              :value="logistics.dataName"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="物流单号">
          <el-input
            v-model="queryParams.logisticsNumber"
            placeholder="请输入物流单号"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 发货单列表 -->
      <el-table
        v-loading="loading"
        :data="deliveryList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="deliveryOrderNumber" label="发货单号" width="150" />
        <el-table-column prop="customerName" label="客户姓名" width="150" />
        <el-table-column prop="warehouseName" label="仓库" width="120">
          <template #default="{ row }">
            {{ row.warehouseName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="deliveryStatus" label="发货状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.deliveryStatus)">
              {{ getStatusName(row.deliveryStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="deliveryDate" label="发货日期" width="120">
          <template #default="{ row }">
            {{ formatDate(row.deliveryDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="logisticsCompany" label="物流公司" width="150">
          <template #default="{ row }">
            {{ row.logisticsCompany || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="logisticsNumber" label="物流单号" width="180">
          <template #default="{ row }">
            {{ row.logisticsNumber || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="totalAmount" label="金额" width="100">
          <template #default="{ row }">
            {{ row.totalAmount ? '¥' + row.totalAmount : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="{ row }">
            <el-button type="info" size="small" @click="handleViewDetail(row)">详情</el-button>
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-tooltip
              v-if="row.deliveryStatus === 'DELIVERED' || row.deliveryStatus === 'RECEIVED'"
              content="已发货或已签收状态不允许删除发货单"
              placement="top"
            >
              <template #default>
                <el-button 
                  type="danger" 
                  size="small" 
                  disabled
                >删除</el-button>
              </template>
            </el-tooltip>
            <el-button 
              v-else
              type="danger" 
              size="small" 
              @click="handleDelete(row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑发货单对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="1000px"
      @close="handleDialogClose"
    >
      <el-form
        ref="deliveryFormRef"
        :model="deliveryForm"
        :rules="deliveryFormRules"
        label-width="120px"
      >
        <el-form-item label="发货单号" prop="deliveryOrderNumber">
          <el-input v-model="deliveryForm.deliveryOrderNumber" />
        </el-form-item>
        <el-form-item label="销售订单号" prop="salesOrderNumber">
          <el-input v-model="deliveryForm.salesOrderNumber" />
        </el-form-item>
        <el-form-item label="客户姓名" prop="customerName">
          <el-input
            v-model="deliveryForm.customerName"
            placeholder="请输入客户姓名或点击右侧按钮选择"
          >
            <template #append>
              <el-button @click="handleOpenCustomerPicker" :icon="Search">选择</el-button>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="仓库" prop="warehouseId">
          <el-select
            v-model="deliveryForm.warehouseId"
            placeholder="请选择仓库"
            clearable
            style="width: 100%"
            @change="handleWarehouseChange"
          >
            <el-option
              v-for="warehouse in warehouseList"
              :key="warehouse.id"
              :label="warehouse.warehouseName"
              :value="warehouse.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="商品明细" prop="items">
          <div style="width: 100%">
            <el-button type="primary" size="small" @click="handleAddProduct">添加商品</el-button>
            <el-table :data="deliveryForm.items" border style="width: 100%; margin-top: 10px" max-height="300">
              <el-table-column prop="productName" label="商品名称" width="150" />
              <el-table-column label="库存" width="100">
                <template #default="{ row }">
                  {{ row.stock !== undefined ? row.stock : '-' }}
                </template>
              </el-table-column>
              <el-table-column label="数量" width="120">
                <template #default="{ row, $index }">
                  <el-input-number
                    v-model="row.quantity"
                    :min="1"
                    :max="row.stock || 999999"
                    :precision="0"
                    size="small"
                    style="width: 100%"
                    @change="handleQuantityChange($index)"
                  />
                </template>
              </el-table-column>
              <el-table-column label="单价" width="120">
                <template #default="{ row }">
                  {{ row.unitPrice ? '¥' + row.unitPrice.toFixed(2) : '-' }}
                </template>
              </el-table-column>
              <el-table-column label="小计" width="120">
                <template #default="{ row }">
                  {{ row.totalPrice ? '¥' + row.totalPrice.toFixed(2) : '-' }}
                </template>
              </el-table-column>
              <el-table-column label="操作" width="80" fixed="right">
                <template #default="{ $index }">
                  <el-button type="danger" size="small" @click="handleRemoveProduct($index)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
            <div style="text-align: right; margin-top: 10px; font-weight: bold">
              合计金额：{{ calculateTotalAmount() }}
            </div>
          </div>
        </el-form-item>
        <el-form-item label="发货状态" prop="deliveryStatus">
          <el-select 
            v-model="deliveryForm.deliveryStatus" 
            style="width: 100%"
            @change="handleDeliveryStatusChange"
          >
            <el-option label="待发货" value="PENDING" />
            <el-option label="已发货" value="DELIVERED" />
            <el-option label="已签收" value="RECEIVED" />
          </el-select>
        </el-form-item>
        <el-form-item label="发货日期" prop="deliveryDate">
          <el-date-picker
            v-model="deliveryForm.deliveryDate"
            type="date"
            placeholder="选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="收货地址" prop="deliveryAddress">
          <el-input v-model="deliveryForm.deliveryAddress" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="联系人" prop="contactPerson">
          <el-input v-model="deliveryForm.contactPerson" />
        </el-form-item>
        <el-form-item label="联系电话" prop="contactPhone">
          <el-input v-model="deliveryForm.contactPhone" />
        </el-form-item>
        <el-form-item label="物流公司" prop="logisticsCompany">
          <el-select
            v-model="deliveryForm.logisticsCompany"
            placeholder="请选择物流公司"
            clearable
            filterable
            style="width: 100%"
          >
            <el-option
              v-for="logistics in logisticsCompanyList"
              :key="logistics.dataCode"
              :label="`${logistics.dataName}(${logistics.dataCode})`"
              :value="logistics.dataName"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="物流单号" prop="logisticsNumber">
          <el-input v-model="deliveryForm.logisticsNumber" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="deliveryForm.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>

    <!-- 详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="发货单详情"
      width="1000px"
      class="delivery-detail-dialog"
    >
      <div v-if="detailData" class="delivery-detail-content">
        <!-- 基本信息卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <el-icon><Document /></el-icon>
              <span>基本信息</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="发货单号" label-class-name="detail-label">
              <span class="detail-value highlight">{{ detailData.deliveryOrderNumber }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="销售订单号" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.salesOrderNumber || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="客户姓名" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.customerName }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="仓库" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.warehouseName || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="发货状态" label-class-name="detail-label">
              <el-tag :type="getStatusType(detailData.deliveryStatus)" size="large">
                {{ getStatusName(detailData.deliveryStatus) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="发货日期" label-class-name="detail-label">
              <span class="detail-value">{{ formatDate(detailData.deliveryDate) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="金额" label-class-name="detail-label">
              <span class="detail-value amount">{{ detailData.totalAmount ? '¥' + detailData.totalAmount.toFixed(2) : '-' }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 物流信息卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <el-icon><Van /></el-icon>
              <span>物流信息</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="物流公司" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.logisticsCompany || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="物流单号" label-class-name="detail-label">
              <span class="detail-value highlight">{{ detailData.logisticsNumber || '-' }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 收货信息卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <el-icon><Location /></el-icon>
              <span>收货信息</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="收货地址" label-class-name="detail-label" :span="2">
              <span class="detail-value">{{ detailData.deliveryAddress || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="联系人" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.contactPerson || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="联系电话" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.contactPhone || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="备注" label-class-name="detail-label" :span="2">
              <span class="detail-value">{{ detailData.remark || '-' }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 商品明细卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <el-icon><Goods /></el-icon>
              <span>商品明细</span>
            </div>
          </template>
          <el-table 
            :data="detailData.items" 
            border 
            style="width: 100%" 
            v-if="detailData.items && detailData.items.length > 0"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
            :row-style="{ height: '50px' }"
            stripe
          >
            <el-table-column prop="productCode" label="商品编码" width="130" align="center" />
            <el-table-column prop="productName" label="商品名称" min-width="180" />
            <el-table-column prop="spec" label="规格" width="120" align="center" />
            <el-table-column prop="unit" label="单位" width="80" align="center" />
            <el-table-column prop="quantity" label="数量" width="100" align="center">
              <template #default="{ row }">
                <span class="quantity-text">{{ row.quantity }}</span>
              </template>
            </el-table-column>
            <el-table-column label="单价" width="120" align="right">
              <template #default="{ row }">
                <span class="price-text">{{ row.unitPrice ? '¥' + row.unitPrice.toFixed(2) : '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="小计" width="120" align="right">
              <template #default="{ row }">
                <span class="total-text">{{ row.totalPrice ? '¥' + row.totalPrice.toFixed(2) : '-' }}</span>
              </template>
            </el-table-column>
          </el-table>
          <div v-else class="empty-products">
            <el-empty description="暂无商品明细" :image-size="80" />
          </div>
        </el-card>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button type="primary" size="large" @click="detailDialogVisible = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 商品选择对话框 -->
    <el-dialog
      v-model="productPickerVisible"
      title="选择商品"
      width="900px"
    >
      <div style="margin-bottom: 10px; color: #909399; font-size: 12px">
        {{ deliveryForm.warehouseId ? '当前仓库商品列表' : '当前企业所有商品列表' }}
      </div>
      <!-- 商品查询表单 -->
      <el-form :inline="true" :model="productQueryParams" class="search-form" style="margin-bottom: 15px">
        <el-form-item label="关键词">
          <el-input
            v-model="productQueryParams.keyword"
            placeholder="商品名称/编码/规格"
            clearable
            @keyup.enter="handleProductSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleProductSearch">查询</el-button>
          <el-button @click="handleProductReset">重置</el-button>
        </el-form-item>
      </el-form>
      <el-table
        :data="productList"
        border
        style="width: 100%"
        v-loading="productLoading"
      >
        <el-table-column prop="productCode" label="商品编码" width="120" />
        <el-table-column prop="productName" label="商品名称" width="150" />
        <el-table-column prop="spec" label="规格" width="120" />
        <el-table-column prop="unit" label="单位" width="80" />
        <el-table-column prop="salePrice" label="销售价" width="100">
          <template #default="{ row }">
            {{ row.salePrice ? '¥' + row.salePrice : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="stock" label="库存" width="100">
          <template #default="{ row }">
            <span :style="{ color: (row.stock || 0) === 0 ? '#F56C6C' : '' }">
              {{ row.stock !== undefined ? row.stock : '-' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <el-button 
              type="primary" 
              size="small" 
              :disabled="(row.stock || 0) === 0"
              @click="selectProduct(row)"
            >
              选择
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <!-- 分页 -->
      <div class="pagination" style="margin-top: 15px">
        <el-pagination
          v-model:current-page="productQueryParams.page"
          v-model:page-size="productQueryParams.pageSize"
          :total="productTotal"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleProductSizeChange"
          @current-change="handleProductPageChange"
        />
      </div>
    </el-dialog>

    <!-- 客户选择对话框 -->
    <el-dialog
      v-model="customerPickerVisible"
      title="选择客户"
      width="800px"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      @close="handleCustomerDialogClose"
    >
      <div style="margin-bottom: 10px; color: #909399; font-size: 12px">
        当前企业下的客户列表
      </div>
      <!-- 客户查询表单 -->
      <el-form :inline="true" :model="customerQueryParams" class="search-form" style="margin-bottom: 15px" @submit.prevent>
        <el-form-item label="客户姓名">
          <el-input
            v-model="customerQueryParams.customerName"
            placeholder="请输入客户姓名"
            clearable
            @keyup.enter="handleCustomerSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="联系电话">
          <el-input
            v-model="customerQueryParams.contactPhone"
            placeholder="请输入联系电话"
            clearable
            @keyup.enter="handleCustomerSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click.prevent="handleCustomerSearch">查询</el-button>
          <el-button @click.prevent="handleCustomerReset">重置</el-button>
        </el-form-item>
      </el-form>
      <el-table
        :data="customerList"
        border
        style="width: 100%"
        v-loading="customerLoading"
        highlight-current-row
      >
        <el-table-column prop="customerName" label="客户姓名" width="150" />
        <el-table-column prop="contactPhone" label="联系电话" width="150" />
        <el-table-column prop="companyName" label="公司名称" />
        <el-table-column prop="address" label="地址" />
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <el-button 
              type="primary" 
              size="small" 
              native-type="button"
              @click.stop.prevent="handleSelectCustomer(row, $event)"
            >选择</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination" style="margin-top: 15px">
        <el-pagination
          v-model:current-page="customerQueryParams.page"
          v-model:page-size="customerQueryParams.pageSize"
          :total="customerTotal"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleCustomerSizeChange"
          @current-change="handleCustomerPageChange"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Document, Van, Location, Goods, Search } from '@element-plus/icons-vue'
import {
  getDeliveryOrderList,
  getDeliveryOrderDetail,
  createDeliveryOrder,
  updateDeliveryOrder,
  deleteDeliveryOrder,
  getDictDataByCode,
  exportDeliveryOrderList
} from '../api/internalDelivery'
import { getWarehouseList } from '../api/internalWarehouse'
import { getProductList } from '../api/internalProduct'
import { getCustomerList } from '../api/internalDelivery'

// 物流公司字典列表
const logisticsCompanyList = ref([])

// 数据
const loading = ref(false)
const deliveryList = ref([])
const total = ref(0)
const exportLoading = ref(false)
// 上次导出时间，用于限流
const lastExportTime = ref(0)
// 导出限流间隔时间（毫秒），3秒内不允许重复导出
const EXPORT_THROTTLE_INTERVAL = 3000
const warehouseList = ref([])
const productList = ref([])
const productTotal = ref(0)
const productLoading = ref(false)
const productPickerVisible = ref(false)
const previousWarehouseId = ref(null)
const productQueryParams = reactive({
  keyword: '',
  page: 1,
  pageSize: 10
})
// 客户选择相关
const customerPickerVisible = ref(false)
const customerList = ref([])
const customerTotal = ref(0)
const customerLoading = ref(false)
// 标记是否正在选择客户，防止弹窗关闭事件触发刷新
const isSelectingCustomer = ref(false)
const customerQueryParams = reactive({
  customerName: '',
  contactPhone: '',
  page: 1,
  pageSize: 10
})
const queryParams = reactive({
  keyword: '',
  deliveryStatus: '',
  logisticsCompany: '',
  logisticsNumber: '',
  page: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const dialogTitle = ref('新增发货单')
const isEdit = ref(false)
const deliveryFormRef = ref(null)
const detailDialogVisible = ref(false)
const detailData = ref(null)
const deliveryForm = reactive({
  id: null,
  deliveryOrderNumber: '',
  salesOrderId: null,
  salesOrderNumber: '',
  customerId: null,
  customerName: '',
  warehouseId: null,
  warehouseName: '',
  deliveryStatus: 'PENDING',
  deliveryDate: null,
  deliveryAddress: '',
  contactPerson: '',
  contactPhone: '',
  logisticsCompany: '',
  logisticsNumber: '',
  totalAmount: null,
  remark: '',
  items: []
})

// 自定义验证器：当发货状态为已发货或已签收时，物流公司和物流单号必填
const validateLogisticsCompany = (rule, value, callback) => {
  if ((deliveryForm.deliveryStatus === 'DELIVERED' || deliveryForm.deliveryStatus === 'RECEIVED') && !value) {
    callback(new Error('发货状态为已发货或已签收时，物流公司必填'))
  } else {
    callback()
  }
}

const validateLogisticsNumber = (rule, value, callback) => {
  if ((deliveryForm.deliveryStatus === 'DELIVERED' || deliveryForm.deliveryStatus === 'RECEIVED') && !value) {
    callback(new Error('发货状态为已发货或已签收时，物流单号必填'))
  } else {
    callback()
  }
}

// 自定义验证器：商品明细必填
const validateItems = (rule, value, callback) => {
  if (!value || value.length === 0) {
    callback(new Error('商品明细必填，请至少添加一条商品'))
  } else {
    callback()
  }
}

const deliveryFormRules = {
  deliveryOrderNumber: [{ required: true, message: '请输入发货单号', trigger: 'blur' }],
  customerName: [{ required: true, message: '请输入客户姓名', trigger: 'blur' }],
  deliveryDate: [{ required: true, message: '请选择发货日期', trigger: 'change' }],
  deliveryAddress: [{ required: true, message: '请输入收货地址', trigger: 'blur' }],
  contactPerson: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
  contactPhone: [{ required: true, message: '请输入联系电话', trigger: 'blur' }],
  logisticsCompany: [{ validator: validateLogisticsCompany, trigger: ['blur', 'change'] }],
  logisticsNumber: [{ validator: validateLogisticsNumber, trigger: ['blur', 'change'] }],
  items: [{ validator: validateItems, trigger: 'change' }]
}

// 方法
const loadWarehouseList = async () => {
  try {
    const res = await getWarehouseList({ page: 1, pageSize: 1000 })
    if (res.code === '000000') {
      warehouseList.value = res.data.list || []
    }
  } catch (error) {
    console.error('获取仓库列表失败:', error)
  }
}

// 加载物流公司字典数据
const loadLogisticsCompanyList = async () => {
  try {
    const res = await getDictDataByCode('logistics_company')
    if (res.code === '000000') {
      logisticsCompanyList.value = res.data || []
    }
  } catch (error) {
    console.error('获取物流公司字典失败:', error)
  }
}

const getStatusName = (status) => {
  const statusMap = {
    'PENDING': '待发货',
    'DELIVERED': '已发货',
    'RECEIVED': '已签收'
  }
  return statusMap[status] || status
}

const getStatusType = (status) => {
  const typeMap = {
    'PENDING': 'warning',
    'DELIVERED': 'info',
    'RECEIVED': 'success'
  }
  return typeMap[status] || ''
}

const formatDate = (date) => {
  if (!date) return '-'
  const d = new Date(date)
  return d.toLocaleDateString('zh-CN')
}

const loadDeliveryList = async () => {
  loading.value = true
  try {
    const params = { ...queryParams }
    const res = await getDeliveryOrderList(params)
    if (res.code === '000000') {
      deliveryList.value = res.data.list || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取发货单列表失败:', error)
    ElMessage.error('获取发货单列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  queryParams.page = 1
  loadDeliveryList()
}

const handleReset = () => {
  queryParams.keyword = ''
  queryParams.deliveryStatus = ''
  queryParams.logisticsCompany = ''
  queryParams.logisticsNumber = ''
  queryParams.page = 1
  loadDeliveryList()
}

// 生成发货单号
const generateDeliveryOrderNumber = () => {
  const now = new Date()
  const year = now.getFullYear().toString()
  const month = (now.getMonth() + 1).toString().padStart(2, '0')
  const day = now.getDate().toString().padStart(2, '0')
  const timestamp = now.getTime().toString().slice(-6)
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
  return `DO${year}${month}${day}${timestamp}${random}`
}

const handleCreate = () => {
  dialogTitle.value = '新增发货单'
  isEdit.value = false
  previousWarehouseId.value = null
  Object.assign(deliveryForm, {
    id: null,
    deliveryOrderNumber: generateDeliveryOrderNumber(),
    salesOrderId: null,
    salesOrderNumber: '',
    customerId: null,
    customerName: '',
    warehouseId: null,
    warehouseName: '',
    deliveryStatus: 'PENDING',
    deliveryDate: null,
    deliveryAddress: '',
    contactPerson: '',
    contactPhone: '',
    logisticsCompany: '',
    logisticsNumber: '',
    totalAmount: null,
    remark: '',
    items: []
  })
  dialogVisible.value = true
}

const handleViewDetail = async (row) => {
  try {
    const res = await getDeliveryOrderDetail(row.id)
    if (res.code === '000000') {
      detailData.value = res.data
      detailDialogVisible.value = true
    }
  } catch (error) {
    console.error('获取发货单详情失败:', error)
    ElMessage.error('获取发货单详情失败')
  }
}

const handleEdit = async (row) => {
  dialogTitle.value = '编辑发货单'
  isEdit.value = true
  try {
    const res = await getDeliveryOrderDetail(row.id)
    if (res.code === '000000') {
      Object.assign(deliveryForm, res.data)
      // 保存当前的仓库ID
      previousWarehouseId.value = deliveryForm.warehouseId
      // 编辑时需要重新加载商品库存信息
      if (deliveryForm.items && deliveryForm.items.length > 0) {
        await refreshProductStock()
      }
      dialogVisible.value = true
    }
  } catch (error) {
    console.error('获取发货单详情失败:', error)
    ElMessage.error('获取发货单详情失败')
  }
}

// 刷新商品库存信息
const refreshProductStock = async () => {
  try {
    const params = { page: 1, pageSize: 1000 }
    if (deliveryForm.warehouseId) {
      params.warehouseId = deliveryForm.warehouseId
    }
    const res = await getProductList(params)
    if (res.code === '000000') {
      const productMap = {}
      res.data.list.forEach(product => {
        productMap[product.id] = product.stock || 0
      })
      // 更新明细中的库存信息
      deliveryForm.items.forEach(item => {
        if (item.productId && productMap[item.productId] !== undefined) {
          item.stock = productMap[item.productId]
          // 如果数量超过库存，调整为库存数量
          if (item.quantity > item.stock) {
            item.quantity = item.stock
            calculateItemTotal(deliveryForm.items.indexOf(item))
          }
        }
      })
    }
  } catch (error) {
    console.error('刷新商品库存失败:', error)
  }
}

const handleSubmit = async () => {
  if (!deliveryFormRef.value) return
  await deliveryFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        // 处理日期格式：如果只有日期（YYYY-MM-DD），转换为日期时间格式（YYYY-MM-DD HH:mm:ss）
        const submitData = { ...deliveryForm }
        if (submitData.deliveryDate && submitData.deliveryDate.length === 10) {
          submitData.deliveryDate = submitData.deliveryDate + ' 00:00:00'
        }
        
        const res = isEdit.value 
          ? await updateDeliveryOrder(submitData) 
          : await createDeliveryOrder(submitData)
        if (res.code === '000000') {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          loadDeliveryList()
        }
      } catch (error) {
        console.error('保存发货单失败:', error)
        const errorData = error.response?.data
        // 检查是否是参数验证错误（code === '000002'）
        if (errorData?.code === '000002' && errorData?.data) {
          // 参数验证失败，解析错误信息并弹窗提示
          const errorMessages = Object.values(errorData.data).join('；')
          ElMessageBox.alert(errorMessages, '参数验证失败', {
            type: 'error',
            confirmButtonText: '确定'
          })
        } else {
          // 其他错误，使用普通消息提示
          ElMessage.error(errorData?.msg || errorData?.message || '保存发货单失败')
        }
      }
    }
  })
}

const handleDialogClose = () => {
  deliveryFormRef.value?.resetFields()
}

// 处理发货状态变化，重新验证物流相关字段和商品明细
const handleDeliveryStatusChange = () => {
  if (deliveryFormRef.value) {
    // 当发货状态改变时，重新验证物流公司、物流单号和商品明细
    deliveryFormRef.value.validateField('logisticsCompany')
    deliveryFormRef.value.validateField('logisticsNumber')
    deliveryFormRef.value.validateField('items')
  }
}

const handleDelete = async (row) => {
  // 检查发货单状态：已发货或已签收状态不允许删除
  if (row.deliveryStatus === 'DELIVERED' || row.deliveryStatus === 'RECEIVED') {
    ElMessage.warning('已发货或已签收状态的发货单不允许删除')
    return
  }
  
  try {
    await ElMessageBox.confirm('确定要删除该发货单吗？', '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    const res = await deleteDeliveryOrder(row.id)
    if (res.code === '000000') {
      ElMessage.success('删除成功')
      loadDeliveryList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除发货单失败:', error)
      ElMessage.error(error.response?.data?.message || '删除发货单失败')
    }
  }
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadDeliveryList()
}

const handlePageChange = (page) => {
  queryParams.page = page
  loadDeliveryList()
}

// 导出发货单列表
const handleExport = async () => {
  // 限流检查：如果正在导出，直接返回
  if (exportLoading.value) {
    return
  }

  // 限流检查：如果距离上次导出时间太短，提示用户
  const now = Date.now()
  const timeSinceLastExport = now - lastExportTime.value
  if (lastExportTime.value > 0 && timeSinceLastExport < EXPORT_THROTTLE_INTERVAL) {
    const remainingSeconds = Math.ceil((EXPORT_THROTTLE_INTERVAL - timeSinceLastExport) / 1000)
    ElMessage.warning(`导出操作过于频繁，请等待 ${remainingSeconds} 秒后再试`)
    return
  }
  
  try {
    exportLoading.value = true
    
    const exportParams = {
      keyword: queryParams.keyword,
      deliveryStatus: queryParams.deliveryStatus,
      logisticsCompany: queryParams.logisticsCompany,
      logisticsNumber: queryParams.logisticsNumber
    }
    
    const blob = await exportDeliveryOrderList(exportParams)
    
    if (!blob || blob.size === 0) {
      throw new Error('导出失败：返回数据格式错误')
    }
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `发货单列表_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${Date.now()}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
    // 导出成功后才记录时间戳，失败时不限流
    lastExportTime.value = Date.now()
  } catch (error) {
    console.error('导出失败:', error)
    const errorMsg = error.message || '导出失败'
    // 如果是限流错误（数据量过大），使用warning提示
    if (errorMsg.includes('数据量过大') || errorMsg.includes('超过最大限制')) {
      ElMessage.warning(errorMsg)
    } else {
      ElMessage.error(errorMsg)
    }
    // 导出失败时不更新时间戳，允许用户立即重试
  } finally {
    exportLoading.value = false
  }
}

// 仓库变更处理
const handleWarehouseChange = (newWarehouseId) => {
  // 如果已选择商品，提示用户
  if (deliveryForm.items.length > 0) {
    ElMessageBox.confirm('切换仓库将清空已选择的商品，是否继续？', '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    }).then(() => {
      deliveryForm.items = []
      calculateTotalAmount()
      // 清空商品列表，下次打开选择对话框时会重新加载
      productList.value = []
      previousWarehouseId.value = newWarehouseId
    }).catch(() => {
      // 取消切换，恢复原来的仓库ID
      deliveryForm.warehouseId = previousWarehouseId.value
    })
  } else {
    // 没有选择商品，直接清空商品列表
    productList.value = []
    previousWarehouseId.value = newWarehouseId
  }
}

// 商品选择相关方法
const loadProductList = async () => {
  productLoading.value = true
  try {
    const params = {
      page: productQueryParams.page,
      pageSize: productQueryParams.pageSize
    }
    // 如果选择了仓库，按仓库ID过滤商品；否则查询所有商品
    if (deliveryForm.warehouseId) {
      params.warehouseId = deliveryForm.warehouseId
    }
    // 如果有查询关键词，添加关键词参数
    if (productQueryParams.keyword) {
      params.keyword = productQueryParams.keyword
    }
    const res = await getProductList(params)
    if (res.code === '000000') {
      productList.value = res.data.list || []
      productTotal.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取商品列表失败:', error)
    ElMessage.error('获取商品列表失败')
  } finally {
    productLoading.value = false
  }
}

const handleAddProduct = () => {
  // 重置查询参数
  productQueryParams.keyword = ''
  productQueryParams.page = 1
  productQueryParams.pageSize = 10
  productPickerVisible.value = true
  loadProductList()
}

// 商品查询相关函数
const handleProductSearch = () => {
  productQueryParams.page = 1
  loadProductList()
}

const handleProductReset = () => {
  productQueryParams.keyword = ''
  productQueryParams.page = 1
  loadProductList()
}

const handleProductSizeChange = (size) => {
  productQueryParams.pageSize = size
  productQueryParams.page = 1
  loadProductList()
}

const handleProductPageChange = (page) => {
  productQueryParams.page = page
  loadProductList()
}

const selectProduct = (product) => {
  // 检查库存
  const stock = product.stock || 0
  if (stock === 0) {
    ElMessageBox.alert('库存不足，请及时补货！', '提示', {
      type: 'warning',
      confirmButtonText: '确定'
    })
    return
  }
  
  // 检查是否已添加
  const exists = deliveryForm.items.find(item => item.productId === product.id)
  if (exists) {
    ElMessage.warning('该商品已添加，请修改数量')
    return
  }
  
  // 添加商品到明细
  deliveryForm.items.push({
    productId: product.id,
    productCode: product.productCode,
    productName: product.productName,
    spec: product.spec || '',
    unit: product.unit || '',
    stock: stock, // 保存库存信息
    quantity: 1,
    unitPrice: product.salePrice || 0,
    totalPrice: product.salePrice || 0,
    remark: ''
  })
  productPickerVisible.value = false
  calculateTotalAmount()
  // 重新验证商品明细字段
  if (deliveryFormRef.value) {
    deliveryFormRef.value.validateField('items')
  }
}

const handleQuantityChange = (index) => {
  const item = deliveryForm.items[index]
  if (item && item.quantity && item.stock !== undefined) {
    if (item.quantity > item.stock) {
      ElMessage.warning(`数量不能超过库存(${item.stock})`)
      item.quantity = item.stock
    }
  }
  calculateItemTotal(index)
}

const handleRemoveProduct = (index) => {
  deliveryForm.items.splice(index, 1)
  calculateTotalAmount()
  // 重新验证商品明细字段
  if (deliveryFormRef.value) {
    deliveryFormRef.value.validateField('items')
  }
}

const calculateItemTotal = (index) => {
  const item = deliveryForm.items[index]
  if (item && item.quantity && item.unitPrice) {
    item.totalPrice = item.quantity * item.unitPrice
  }
  calculateTotalAmount()
}

const calculateTotalAmount = () => {
  let total = 0
  deliveryForm.items.forEach(item => {
    if (item.totalPrice) {
      total += item.totalPrice
    }
  })
  deliveryForm.totalAmount = total
  return '¥' + total.toFixed(2)
}

// 客户选择相关方法
const loadCustomerList = async () => {
  customerLoading.value = true
  try {
    const params = {
      page: customerQueryParams.page,
      pageSize: customerQueryParams.pageSize
    }
    if (customerQueryParams.customerName) {
      params.customerName = customerQueryParams.customerName
    }
    if (customerQueryParams.contactPhone) {
      params.contactPhone = customerQueryParams.contactPhone
    }
    const res = await getCustomerList(params)
    if (res.code === '000000') {
      customerList.value = res.data.list || []
      customerTotal.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取客户列表失败:', error)
    ElMessage.error('获取客户列表失败')
  } finally {
    customerLoading.value = false
  }
}

const handleOpenCustomerPicker = () => {
  // 重置查询参数
  customerQueryParams.customerName = ''
  customerQueryParams.contactPhone = ''
  customerQueryParams.page = 1
  customerQueryParams.pageSize = 10
  customerPickerVisible.value = true
  loadCustomerList()
}

const handleCustomerSearch = () => {
  customerQueryParams.page = 1
  loadCustomerList()
}

const handleCustomerReset = () => {
  customerQueryParams.customerName = ''
  customerQueryParams.contactPhone = ''
  customerQueryParams.page = 1
  loadCustomerList()
}

const handleSelectCustomer = (row, event) => {
  // 第一步：立即阻止所有默认行为和事件冒泡（必须在最开始执行）
  if (event) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    // 阻止事件继续传播
    if (event.cancelBubble !== undefined) {
      event.cancelBubble = true
    }
  }
  
  // 第二步：验证数据
  if (!row || !row.id) {
    return false
  }
  
  // 第三步：设置选择标记，防止弹窗关闭事件触发刷新
  isSelectingCustomer.value = true
  
  // 第四步：立即回显客户信息（同步操作）
  deliveryForm.customerId = row.id
  deliveryForm.customerName = row.customerName || row.contactName || ''
  // 如果客户有联系电话，也可以自动填充
  if (row.contactPhone && !deliveryForm.contactPhone) {
    deliveryForm.contactPhone = row.contactPhone
  }
  
  // 第五步：立即关闭弹窗（同步操作）
  customerPickerVisible.value = false
  
  // 第六步：延迟重置标记，确保弹窗关闭事件处理完成
  setTimeout(() => {
    isSelectingCustomer.value = false
  }, 200)
  
  // 返回false进一步阻止事件
  return false
}

// 处理客户选择弹窗关闭事件
const handleCustomerDialogClose = () => {
  // 如果正在选择客户，不执行任何操作，避免触发列表刷新
  if (isSelectingCustomer.value) {
    return
  }
  // 用户手动关闭弹窗时，也不执行任何操作
}

const handleCustomerSizeChange = (size) => {
  customerQueryParams.pageSize = size
  customerQueryParams.page = 1
  loadCustomerList()
}

const handleCustomerPageChange = (page) => {
  customerQueryParams.page = page
  loadCustomerList()
}

// 生命周期
onMounted(() => {
  loadWarehouseList()
  loadLogisticsCompanyList()
  loadDeliveryList()
})
</script>

<style scoped>
.delivery-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.product-items {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.product-tag {
  margin: 0;
  font-size: 12px;
}

.empty-text {
  color: #909399;
}

/* 详情对话框样式 */
:deep(.delivery-detail-dialog) {
  .el-dialog__body {
    padding: 20px;
    background: #f5f7fa;
  }
}

.delivery-detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-card {
  border-radius: 8px;
  overflow: hidden;
}

.detail-card :deep(.el-card__header) {
  color: #fff;
  padding: 15px 20px;
  border: none;
}

.detail-card:nth-child(1) :deep(.el-card__header) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.detail-card:nth-child(2) :deep(.el-card__header) {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.detail-card:nth-child(3) :deep(.el-card__header) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.detail-card:nth-child(4) :deep(.el-card__header) {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.detail-card :deep(.el-card__body) {
  padding: 20px;
  background: #fff;
}

.detail-card :deep(.el-table) {
  border-radius: 4px;
  overflow: hidden;
}

.detail-card :deep(.el-table th) {
  background: #f5f7fa !important;
}

.detail-card :deep(.el-table tr:hover) {
  background: #f0f9ff !important;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
}

.card-title .el-icon {
  font-size: 18px;
}

:deep(.detail-label) {
  background: #fafafa !important;
  font-weight: 600;
  color: #606266;
  width: 120px;
}

.detail-value {
  color: #303133;
  font-size: 14px;
}

.detail-value.highlight {
  color: #409eff;
  font-weight: 600;
  font-size: 15px;
}

.detail-value.amount {
  color: #f56c6c;
  font-weight: 700;
  font-size: 16px;
}

.empty-products {
  padding: 40px 0;
  text-align: center;
}

.quantity-text {
  font-weight: 600;
  color: #409eff;
}

.price-text {
  color: #606266;
  font-weight: 500;
}

.total-text {
  color: #f56c6c;
  font-weight: 700;
  font-size: 15px;
}

.dialog-footer {
  text-align: center;
  padding: 10px 0;
}

.dialog-footer .el-button {
  min-width: 120px;
}
</style>

